import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    addWorkspaceName: string;
    cancel: string;
    title: string;
    editAvatar: string;
    save: string;
    workspaceName: string;
    dialogCloseLabel: string;
    assetLifecyclePolicy: {
        /** Description when asset lifecycle defaults are disabled */
        disabledDescription: string;
        /** Description when asset lifecycle defaults are enabled */
        enabledDescription: (days: number) => string | React.ReactNode;
        /** Inline alert message explaining default policy impact */
        inlineAlert: (days: number) => string | React.ReactNode;
        /** Setting label for asset expiration defaults */
        settingsLabel: string;
    };
    contentSecurityDisclaimer: string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Placeholder text for workspace name input */
            addWorkspaceName: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.addWorkspaceName?c=ee3zvg4pl9r9", defaultMessage: "Add workspace name" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Secondary action label to cancel workspace creation */
            cancel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.cancel?c=2ojhd8rzicmo0", defaultMessage: "Cancel" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Dialog title for creating a workspace */
            title: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.title?c=2fk7c6qpausxv", defaultMessage: "Create New Workspace" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for the avatar editor action */
            editAvatar: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.editAvatar?c=15xb4wbq3buf6", defaultMessage: "Edit workspace avatar" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Primary action label to save the new workspace */
            save: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.save?c=1oof7q3mpz3f9", defaultMessage: "Save" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for the workspace name field */
            workspaceName: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.workspaceName?c=2ew295eir43ng", defaultMessage: "Workspace name" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Accessible label for the dialog close button */
            dialogCloseLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.dialogCloseLabel?c=2g2kiprif3vje", defaultMessage: "Close Dialog" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text labels for asset lifecycle defaults on create */
            assetLifecyclePolicy: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Description when asset lifecycle defaults are disabled */
                        disabledDescription: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.assetLifecyclePolicy.disabledDescription?c=18c4dh3gbt1vr", defaultMessage: "Auto-delete assets after upload date." }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Description when asset lifecycle defaults are enabled */
                        enabledDescription: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (days: number) => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.assetLifecyclePolicy.enabledDescription?c=xdeo7k171nva", defaultMessage: "{days, plural, one {Auto-delete assets 1 day after upload date.} other {Auto-delete assets {days} days after upload date.}}" }, { days: days }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Inline alert message explaining default policy impact */
                        inlineAlert: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (days: number) => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.assetLifecyclePolicy.inlineAlert?c=nxv9o23l5gqo", defaultMessage: "{days, plural, one {Assets uploaded more than 1 day ago will be deleted within 1 day.} other {Assets uploaded more than {days} days ago will be deleted within 1 day.}}" }, { days: days }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Setting label for asset expiration defaults */
                        settingsLabel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.assetLifecyclePolicy.settingsLabel?c=3tm3zfvi5y5yv", defaultMessage: "Asset Expiration" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            /** Disclaimer about inherited content security defaults */
            contentSecurityDisclaimer: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CreateWorkspaceModal.contentSecurityDisclaimer?c=3b5n2qxfsfscd", defaultMessage: "Workspace content security settings will inherit the defaults from your account settings. Update them in workspace settings after the workspace is created." }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
